import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	DistriwinAllAnimalsListSetting,
	DistriwinAllAnimalsWorkListTableItem,
	IMoveEvent,
	WorkListType,
} from 'shared/api/api';
import { getLocationStringNameBySectionId, getPenNameByPenId } from 'shared/helpers/location-helper';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { FlexColumn } from '../../basic-flex/flex-column';
import '../list-setup.scss';

import { calculateDays } from 'shared/helpers/general-helpers';
import { memoizeHashmapLocation } from 'shared/helpers/memoize-getters/memoize-getters';
import { getCycleDays1 } from 'shared/helpers/pregnancy-helper/generel-pregnancy-helpers';
import { getSowState } from 'shared/helpers/pregnancy-helper/sow-state-helper';
import { calculateAnimalKind1 } from 'shared/helpers/stemanimal-helper/stemanimal-helper';
import { SowState } from 'shared/state/models/sow-state';
import SkioldTableGrid from '../../skiold-components/skiold-table/skiold-table-grid/skiold-table-grid';
import { SowListConstants } from '../../stem-animal/animal-lists/table-constants';
interface Props {
	penIds: string[];
	sectionId: string;
	setFilteredData: (data: DistriwinAllAnimalsWorkListTableItem[]) => void;
	setSelected: (data: DistriwinAllAnimalsWorkListTableItem[]) => void;
	skioldRef?: React.MutableRefObject<any>;
}

export const DistriwinAllAnimalsTable: FC<Props> = React.memo(
	({ penIds, sectionId, setFilteredData, setSelected, skioldRef }) => {
		const generalSettings = useSelector((state: WebAppState) => state.generalSettings.entity);
		const worklistSetting = useSelector(
			(state: WebAppState) =>
				state.workListSettings.entities.find(
					setup => setup.type === WorkListType.DistriwinAllAnimalsListSetting,
				)! as DistriwinAllAnimalsListSetting,
		);
		const animals = useSelector((state: WebAppState) => state.stemAnimals.entities);
		const moveEvents = useSelector((state: WebAppState) => state.moveEvents.entities);
		const pregnancyEvents = useSelector((state: WebAppState) => state.pregnancyEvents.entities);
		const hashmapLocations = useSelector((state: WebAppState) =>
			memoizeHashmapLocation(state.locations.buildings, state.locations.sections, state.locations.pens),
		);
		const validationSettings = useSelector((state: WebAppState) =>
			state.validationSetup.entity.validationIntervals &&
			state.validationSetup.entity.validationIntervals &&
			state.validationSetup.entity.validationIntervals[1] !== undefined &&
			state.validationSetup.entity.validationIntervals[1].plan
				? state.validationSetup.entity.validationIntervals[1].plan
				: 116,
		);

		const animalsInPens = useMemo(() => {
			const mappedMoveEvents = moveEvents.keyValuePairSingle2('stemAnimalId');
			const listItems: DistriwinAllAnimalsWorkListTableItem[] = [];
			animals.forEach(animal => {
				let penId = animal.entrancePenId;
				let entranceToPenDate = animal.entranceDate;
				const moveEvent: IMoveEvent = mappedMoveEvents[animal.id!];

				if (moveEvent && moveEvent.penId) {
					penId = moveEvent.penId;
					entranceToPenDate = moveEvent.moveDate;
				}
				if (!penIds.includes(penId!)) {
					return;
				}

				const pregnancies = pregnancyEvents[animal.id!];
				let cycleDays: number | undefined = 0;
				if (pregnancies && pregnancies.length > 0) {
					cycleDays = getCycleDays1(animal.id!, pregnancies);
				} else {
					if (entranceToPenDate) {
						cycleDays = calculateDays(entranceToPenDate, new Date());
					}
				}
				const condition = getSowState(pregnancies);
				if (condition === SowState.Pregnant) {
					cycleDays = (cycleDays ?? 0) - (worklistSetting.negativeCycleDays ? validationSettings : 0);
				}
				listItems.push({
					animalNumber: animal.animalNumber,
					animalKind: localized(calculateAnimalKind1(animal, pregnancies)),
					condition: localized(condition),
					days: cycleDays,
					section: getLocationStringNameBySectionId(sectionId, hashmapLocations),
					pen: getPenNameByPenId(penId, hashmapLocations),
					penId: penId,
				} as DistriwinAllAnimalsWorkListTableItem);
			});
			return listItems;
		}, [animals, moveEvents, penIds, hashmapLocations, pregnancyEvents, validationSettings]);

		const columns = useMemo(() => {
			return [
				{
					name: 'animalNumber',
					title: localized('animalNumber'),
				},
				{
					name: 'animalKind',
					title: localized('kind'),
				},
				{
					name: 'condition',
					title: localized('condition'),
				},
				{
					name: 'days',
					title: localized('Days'),
				},
				{
					name: 'section',
					title: localized('section'),
				},
				{
					name: 'pen',
					title: localized('pen'),
				},
			];
		}, []);
		const columnExt = useMemo(() => {
			return [
				{
					columnName: 'animalNumber',
					width: SowListConstants.animalNrWidth,
				},

				{
					columnName: 'animalKind',
					width: SowListConstants.animalNrWidth,
				},
				{
					columnName: 'condition',
					width: SowListConstants.animalNrWidth,
				},
				{
					columnName: 'days',
					width: SowListConstants.animalNrWidth,
				},
				{
					columnName: 'section',
					width: SowListConstants.animalNrWidth,
				},
				{
					columnName: 'pen',
					width: SowListConstants.animalNrWidth,
				},
			];
		}, []);

		return (
			<FlexColumn className="work-list">
				<SkioldTableGrid
					ref={skioldRef}
					tableKey={'distriwinAllAnimalsTableList'}
					columns={columns}
					ColumnExtensions={columnExt}
					data={animalsInPens}
					onFiltersChanged={setFilteredData}
					useSelection={generalSettings.enableDistriwinPenUpdate}
					selectedChanged={setSelected}
					selectionHeader={localized('completed')}
				/>
			</FlexColumn>
		);
	},
);
